import React from "react";
import { Button, IconButton, Tooltip } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import { X, AlertTriangle } from "lucide-react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useAIProviderHealth from "../../hooks/useAIProviderHealth";
import { providerPanelUrl } from "../../helpers/aiProviderHealthState";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    alertBanner: {
      borderRadius: 10,
      margin: theme.spacing(1.5, 2, 0, 2),
      flexShrink: 0,
      boxShadow: dark ? "0 1px 4px rgba(0,0,0,0.25)" : "none",
      border: `1px solid ${theme.palette.divider}`,
      borderLeft: "4px solid #ef4444",
      backgroundColor: dark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
      color: dark ? "#ef4444" : "#dc2626",
      "& .MuiAlert-message": { color: theme.palette.text.primary },
    },
    alertActions: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    actionButton: {
      color: "inherit",
      borderColor: dark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.23)",
      textTransform: "none",
    },
  };
});

const I18N = { interpolation: { escapeValue: false, skipOnVariables: true } };

const AIProviderHealthAlert = () => {
  const classes = useStyles();
  const history = useHistory();
  const { visible, dismiss, blockSeconds, isAdmin, gateEnabled } = useAIProviderHealth();

  if (!isAdmin || visible.length === 0) return null;

  const minutes = Math.max(1, Math.ceil((Number(blockSeconds) || 300) / 60));

  return (
    <>
      {visible.map((alert) => {
        const configs = alert.affectedConfigurations;
        const names =
          configs.length > 5
            ? `${configs.slice(0, 5).map((c) => c.name).join(", ")} ${i18n.t("aiProviderHealth.andMore", { count: configs.length - 5, ...I18N })}`
            : configs.map((c) => c.name).join(", ");
        const panelUrl = providerPanelUrl(alert.provider);
        const titleKey =
          gateEnabled === false
            ? "aiProviderHealth.titleNoGate"
            : alert.blocking === false
              ? "aiProviderHealth.titleAwaiting"
              : "aiProviderHealth.title";
        const body =
          configs.length === 0
            ? i18n.t("aiProviderHealth.bodyNoAgents", I18N)
            : i18n.t("aiProviderHealth.body", { count: configs.length, names, minutes, ...I18N });
        return (
          <Alert
            key={`${alert.provider}:${alert.openedAt}`}
            severity="error"
            className={classes.alertBanner}
            icon={
              <Tooltip title={alert.detail || ""}>
                <span style={{ display: "inline-flex" }}><AlertTriangle /></span>
              </Tooltip>
            }
            action={
              <div className={classes.alertActions}>
                {panelUrl && (
                  <Button
                    color="inherit"
                    size="small"
                    className={classes.actionButton}
                    href={panelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18n.t("aiProviderHealth.openProviderPanel", I18N)}
                  </Button>
                )}
                <Button
                  color="inherit"
                  size="small"
                  className={classes.actionButton}
                  onClick={() => history.push("/ai-configurations")}
                >
                  {i18n.t("aiProviderHealth.viewAgents", I18N)}
                </Button>
                <IconButton
                  aria-label={i18n.t("aiProviderHealth.dismiss", I18N)}
                  color="inherit"
                  size="small"
                  onClick={() => dismiss(alert.provider)}
                >
                  <X size={16} />
                </IconButton>
              </div>
            }
          >
            <AlertTitle>
              {i18n.t(titleKey, { provider: alert.providerLabel, ...I18N })}
            </AlertTitle>
            {body}
          </Alert>
        );
      })}
    </>
  );
};

export default AIProviderHealthAlert;
